import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, remove } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    FaSearch, 
    FaFileAlt, 
    FaAddressCard, 
    FaUserCircle, 
    FaTrashAlt, 
    FaUpload, 
    FaCopy,
    FaFileExcel,
    FaPrint, FaEye,
    FaArrowLeft
} from 'react-icons/fa';

const AuditEngineers = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTaluka, setSelectedTaluka] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Determine title based on path
    const isNew = location.pathname.includes('/new');
    const pageTitle = isNew ? "नवीन सु.बे. अभियंत्यांची यादी" : "जुने सु.बे. अभियंत्यांची यादी";

    const talukaMapping = {
        'Miraj': 'मिरज',
        'Tasgaon': 'तासगाव',
        'Kavathe Mahankal': 'कवठे महांकाळ',
        'Walwa': 'वाळवा',
        'Shirala': 'शिराळा',
        'Khanapur': 'खानापूर',
        'Atpadi': 'आटपाडी',
        'Jat': 'जत',
        'Kadegaon': 'कडेगाव',
        'Palus': 'पलूस'
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubData = onValue(registrationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                // Real-time data from Firebase
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const filteredData = registrations.filter(item => {
        const fullName = `${item.firstName || ''} ${item.middleName || ''} ${item.surname || ''}`.toLowerCase();
        
        // Status filter: New list shows Pending, Old list shows Accepted
        const role = item.acceptedByRole || "Assistant";
        const statusField = role.toLowerCase().replace(' ', '') + 'Status';
        const roleStatus = item[statusField] || 'pending';
        
        const matchesStatus = isNew ? (roleStatus === 'pending') : (roleStatus === 'accepted');
        const matchesTaluka = !selectedTaluka || item.taluka === selectedTaluka;
        
        return fullName.includes(searchTerm.toLowerCase()) && matchesStatus && matchesTaluka;
    });

    const getStatusText = (status) => {
        if (!status || status === 'pending') return <span className="text-amber-500 font-bold uppercase tracking-tighter">Pending</span>;
        if (status === 'accepted') return <span className="text-green-600 font-bold uppercase tracking-tighter">Accepted</span>;
        if (status === 'rejected') return <span className="text-red-600 font-bold uppercase tracking-tighter">Rejected</span>;
        return <span className="text-amber-500 font-bold uppercase tracking-tighter">Pending</span>;
    };
    
    const handleDelete = async (id) => {
        if (window.confirm("तुम्हाला खात्री आहे की तुम्ही ही नोंदणी हटवू इच्छिता?")) {
            try {
                const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
                await remove(engineerRef);
                toast.success("नोंदणी यशस्वीरीत्या हटवण्यात आली!");
            } catch (error) {
                toast.error("त्रुटी: " + error.message);
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-gray-600 hover:text-blue-600 hover:border-blue-100 hover:bg-blue-50/50 transition-all duration-300 active:scale-95 group"
                        title="मागे जा"
                    >
                        <FaArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 border-l-4 border-[#1a1a4d] pl-4">{pageTitle} (Audit)</h1>
                </div>
                <div className="text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400">{pageTitle}</span>
                </div>
            </div>

            {/* Content Area */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                
                {/* Stats & Legends Header */}
                <div className="p-6 border-b border-gray-50 bg-white flex flex-col lg:flex-row justify-between gap-6">
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-amber-500 shadow-sm shadow-amber-100"></span>
                            <span className="text-xs font-bold text-gray-600 tracking-tight">Pending</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-600 shadow-sm shadow-green-100"></span>
                            <span className="text-xs font-bold text-gray-600 tracking-tight">Accepted</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-red-600 shadow-sm shadow-red-100"></span>
                            <span className="text-xs font-bold text-gray-600 tracking-tight">Rejected</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        <div className="flex items-center gap-2"><FaFileAlt className="text-red-500 text-sm" /> प्रमाणपत्रधारक</div>
                        <div className="flex items-center gap-2"><FaAddressCard className="text-gray-700 text-sm" /> अर्जदाराची संपूर्ण माहिती</div>
                        <div className="flex items-center gap-2"><FaUserCircle className="text-gray-700 text-sm" /> अर्जदाराची कागदपत्रे</div>
                        <div className="flex items-center gap-2"><FaTrashAlt className="text-gray-400 text-sm" /> डिलीट</div>
                        <div className="flex items-center gap-2"><FaUpload className="text-gray-700 text-sm" /> चलन अपलोड केले</div>
                    </div>
                </div>

                {/* Toolbar Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-700 transition active:scale-95"><FaCopy className="text-xs" /> Copy</button>
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-700 transition active:scale-95"><FaFileExcel className="text-xs" /> Excel</button>
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-700 transition active:scale-95"><FaPrint className="text-xs" /> Print</button>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-500 flex-shrink-0">तालुका फिल्टर:</span>
                            <select 
                                className="w-48 pl-4 pr-10 py-2.5 border-2 border-gray-100 rounded-lg text-sm font-medium focus:border-[#1a1a4d] transition-all outline-none bg-gray-50 appearance-none bg-no-repeat bg-[right_1rem_center]"
                                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'currentColor\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundSize: '1em' }}
                                value={selectedTaluka}
                                onChange={(e) => setSelectedTaluka(e.target.value)}
                            >
                                <option value="">सर्व तालुके</option>
                                {Object.entries(talukaMapping).map(([en, mr]) => (
                                    <option key={en} value={en}>{mr}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-4">
                            <span className="text-sm font-bold text-gray-500 flex-shrink-0">Search:</span>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    className="w-64 pl-4 pr-10 py-2.5 border-2 border-gray-100 rounded-lg text-sm font-medium focus:border-[#1a1a4d] transition-all outline-none bg-gray-50"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <FaSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-[#f8f9fa] border-y border-gray-100 text-center">
                            <tr>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r">क्र. ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r min-w-[250px] text-left">अर्जदाराचे नाव ↑↓</th>
                                {isNew && <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r">फोन क्र. ↑↓</th>}
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r">नोंदणी दिनांक ↑↓</th>
                                {isNew && <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center">तालुका स्थिती ↑↓</th>}
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center">Assistant Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center">Accountant Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center">Engineer Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center">Add CEO Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center">CEO Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center">Action ↑↓</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredData.map((item, index) => (
                                <tr 
                                    key={item.id} 
                                    className={`group transition-all ${!isNew && index % 2 === 1 ? 'bg-[#f4f7fe]' : 'bg-white'} hover:bg-blue-50/40`}
                                >
                                    <td className="px-6 py-6 text-sm font-black text-gray-400 border-r text-center italic">{index + 1}</td>
                                    <td className="px-6 py-6 border-r text-left">
                                        <div className="flex flex-col gap-0.5">
                                            <div className="flex items-center gap-3">
                                                <span className="font-bold text-gray-800 text-[15px] group-hover:text-blue-700 transition">
                                                    {item.firstName} {item.middleName} {item.surname}
                                                </span>
                                                <FaFileAlt className="text-red-500 cursor-pointer hover:scale-125 transition duration-200 shrink-0" />
                                            </div>
                                            {!isNew && <span className="text-sm font-bold text-gray-600 tracking-wider font-mono">{item.whatsappNo}</span>}
                                        </div>
                                    </td>
                                    {isNew && <td className="px-6 py-6 text-sm font-bold text-gray-600 border-r tracking-wider text-center">{item.whatsappNo}</td>}
                                    <td className="px-6 py-6 text-sm font-bold text-gray-500 border-r text-center leading-relaxed">
                                        {item.submittedAt ? (
                                            isNew ? (
                                                new Date(item.submittedAt).toLocaleDateString('en-GB', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric'
                                                }).replace(/ /g, '-')
                                            ) : (
                                                <div className="flex flex-col">
                                                    <span>{new Date(item.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}</span>
                                                    <span className="text-[10px] text-gray-400">
                                                        {new Date(item.submittedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                                                    </span>
                                                </div>
                                            )
                                        ) : '-'}
                                    </td>
                                    {isNew && (
                                        <td className="px-6 py-6 text-center border-r">
                                            <div className="flex flex-col items-center gap-1">
                                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                                    {talukaMapping[item.taluka] || item.taluka || '-'}
                                                </span>
                                                {(() => {
                                                    const role = item.acceptedByRole || "Assistant";
                                                    const statusField = role.toLowerCase().replace(' ', '') + 'Status';
                                                    const roleStatus = item[statusField] || 'pending';
                                                    return getStatusText(roleStatus);
                                                })()}
                                            </div>
                                        </td>
                                    )}
                                    <td className={`px-6 py-6 text-center border-r ${isNew ? 'hidden' : ''}`}>{getStatusText(item.assistantStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.accountantStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.engineerStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.adminStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.status)}</td>
                                    <td className="px-6 py-6 font-bold">
                                        <div className="flex items-center gap-4 justify-center">
                                            <FaEye 
                                                className="text-blue-600 text-lg cursor-pointer hover:text-blue-800 hover:scale-110 transition shrink-0" 
                                                title="तपशील पहा" 
                                                onClick={() => navigate(`/audit/engineers/details/${item.id}`)}
                                            />
                                            <FaUserCircle 
                                                className="text-gray-400 text-lg cursor-pointer hover:text-blue-600 hover:scale-110 transition shrink-0" 
                                                title="कागदपत्रे" 
                                                onClick={() => navigate(`/audit/engineers/documents/${item.id}`)}
                                            />
                                            <FaTrashAlt 
                                                className="text-gray-300 text-lg cursor-pointer hover:text-red-500 hover:scale-110 transition shrink-0" 
                                                title="डिलीट" 
                                                onClick={() => handleDelete(item.id)}
                                            />
                                            <FaUpload className="text-gray-400 text-lg cursor-pointer hover:text-green-600 hover:scale-110 transition shrink-0" title="चलन अपलोड" />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredData.length === 0 && !loading && (
                        <div className="py-20 text-center flex flex-col items-center gap-4">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center">
                                <FaSearch className="text-gray-200 text-3xl" />
                            </div>
                            <p className="text-gray-300 font-black uppercase tracking-widest italic">कोणतीही नोंदणी आढळली नाही.</p>
                        </div>
                    )}
                </div>

                {/* Table Footer */}
                <div className="p-6 border-t border-gray-50 bg-gray-50/10 flex justify-between items-center text-xs font-bold text-gray-400 tracking-wider">
                    <div>Showing {filteredData.length} entries</div>
                </div>
            </div>
        </div>
    );
};

export default AuditEngineers;
